import { ParsedContent } from '@/app/types/Generator/ParsedContent';
import Loader from '@/components/common/loader/Loader';
import axios, { AxiosPromise } from 'axios';
import React, { useEffect, useState } from 'react';
import { LearningObjectives } from './learning-objectives/LearningObjectives';
import { useDeck } from '@/app/context/DeckContext/useDeck';
import { CustomInstructions } from './custom-instructions/CustomInstructions';
import { DeckStatus } from '@/app/constants/DeckStatus';
import { useAnalytics } from '@/app/context/AnalyticsContext/useAnalytics';
import { PageViewer } from './page-viewer/PageViewer';
import { getDocument, PDFDocumentProxy } from 'pdfjs-dist/legacy/build/pdf.mjs';
import 'pdfjs-dist/build/pdf.worker.mjs';

interface IProps {
  width: number;
  pages: string[];
  fileType: string;
  visibleSize: string;
  visiblePages?: number[];
  loadingMessage?: string;
  hideCustomOptions?: boolean;
  onParsedContentChange?: (parsedContent: ParsedContent[]) => void;
}

function onlyUnique(value, index, array) {
  return array.indexOf(value) === index;
}

export default function FileViewer(props: IProps) {
  const {
    selectedDeckLearningObjectives,
    selectedDeckCustomInstructions,
    selectedDeck
  } = useDeck();

  const [width, setWidth] = useState(100);

  const [allPageContent, setAllPageContent] = useState<
    Record<string, string | null>
  >({});

  const [parsedContent, setParsedContent] = useState<ParsedContent[]>([]);

  const [blobDictionary, setBlobDictionary] = useState<Record<string, Blob>>(
    {}
  );

  const [pdfDocument, setPdfDocument] = useState<PDFDocumentProxy | null>(null);

  const [view, setView] = useState<number[]>([]);

  const [selectedTab, setSelectedTab] = useState('file');

  const { trackEvent, analyticsInstance } = useAnalytics();

  const preloadBlobs = async () => {
    const uniqueFiles = props.pages.filter(onlyUnique);

    const responsePromises: AxiosPromise<any>[] = [];

    // TODO

    // if there is only 1 file, do a head request and check the file size
    // We should return a memory flag in the case that the file is really large
    // and let the user decide if they want to proceed with loading

    // If the user selected always load, then don't even check
    // If the user selected never load, then hide the window
    // If the user select always ask, then show the prompt

    // if (uniqueFiles.length === 1) {
    //   const result = await axios({
    //     url: uniqueFiles[0], //your url
    //     method: 'GET',
    //     headers: {
    //       Range: 'bytes=0-0'
    //     }
    //   });

    //   console.log(result.headers);
    // }

    for (const uniqueFile of uniqueFiles) {
      const promise = axios({
        url: uniqueFile, //your url
        method: 'GET',
        responseType: 'blob' // important
      });

      responsePromises.push(promise);
    }

    const responses = await Promise.all(responsePromises);

    const blobDictionary: Record<string, Blob> = {};

    uniqueFiles.forEach((file, index) => {
      const blob = responses[index].data;

      blobDictionary[file] = blob;
    });

    setBlobDictionary(blobDictionary);

    if (props.pages.length > 0 && props.pages[0].includes('.pdf')) {
      const doc = await getDocument(
        await blobDictionary[props.pages[0]].arrayBuffer()
      ).promise;

      setPdfDocument(doc);

      await onLoad(blobDictionary[props.pages[0]], 1)();
    }
  };

  useEffect(() => {
    return () => {
      if (pdfDocument) {
        pdfDocument.destroy();
      }
    };
  }, []);

  useEffect(() => {
    if (props.pages && props.pages.length > 0) {
      preloadBlobs();
    }
  }, [props.pages]);

  useEffect(() => {
    const content: Record<string, string> = {};

    for (let i = 1; i <= props.pages.length; i++) {
      content[i] = null;
    }

    setAllPageContent(content);
  }, [props.pages]);

  useEffect(() => {
    if (
      (props.fileType === 'pdf' ||
        props.fileType === 'docx' ||
        props.fileType === 'txt') &&
      parsedContent.length > 0
    ) {
      if (
        (props.visiblePages &&
          parsedContent.every((value) =>
            props.visiblePages.includes(value.index)
          ) &&
          props.visiblePages.length === parsedContent.length) ||
        (!props.visiblePages && parsedContent.length === props.pages.length)
      ) {
        if (props.onParsedContentChange) {
          props.onParsedContentChange(parsedContent);
        }

        return;
      }
    }

    if (props.onParsedContentChange) {
      props.onParsedContentChange(null);
    }
  }, [parsedContent]);

  useEffect(() => {
    if (
      Object.keys(allPageContent).length > 0 &&
      !Object.entries(allPageContent).some(([key, value]) => value === null)
    ) {
      const newParsedContent: ParsedContent[] = [];

      if (props.visiblePages) {
        for (const visiblePage of props.visiblePages) {
          const parsedContentItem: ParsedContent = {
            index: visiblePage,
            name: 'Page ' + visiblePage,
            text: allPageContent[visiblePage]
          };

          newParsedContent.push(parsedContentItem);
        }
      } else {
        for (let i = 1; i <= props.pages.length; i++) {
          const parsedContentItem: ParsedContent = {
            index: i,
            name: 'Page ' + i,
            text: allPageContent[i]
          };

          newParsedContent.push(parsedContentItem);
        }
      }

      setParsedContent(newParsedContent);
    }
  }, [allPageContent, props.visiblePages]);

  const onLoad = (blob: Blob, pageNumber: number) => async () => {
    calculateWidth();

    // Only need to load all the pages once
    if (pageNumber === 1) {
      const doc = await getDocument(await blob.arrayBuffer()).promise;

      const content = {};

      const firstPage = await doc.getPage(1);

      setView(firstPage.view);

      const laodPageText = (pageNumber: number) => {
        return new Promise(async (resolve) => {
          const page = await doc.getPage(pageNumber);

          const textContent = await page.getTextContent();

          const text = textContent.items
            .filter((item: any) => typeof item.str === 'string')
            .map((item: any) => {
              if (item.hasEOL) {
                return item.str + ' ';
              }
              return item.str;
            })
            .join('');

          resolve(text);
        });
      };

      const pageTextPromises = [];

      for (let i = 1; i <= props.pages.length; i++) {
        pageTextPromises.push(laodPageText(i));
      }

      const pageTextResult = await Promise.all(pageTextPromises);

      for (let i = 1; i <= props.pages.length; i++) {
        content[i] = pageTextResult[i - 1];
      }

      setAllPageContent(content);

      doc.destroy();
    }
  };

  const calculateWidth = () => {
    const elements = document.getElementsByClassName('file-container');
    if (elements.length > 0) {
      setWidth(elements[0].clientWidth);
    }
  };

  useEffect(() => {
    function handleResize() {
      calculateWidth();
    }
    // Add event listener
    window.addEventListener('resize', handleResize);
    // Call handler right away so state gets updated with initial window size
    handleResize();
    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const options = (
    <div
      className="absolute mt-2 w-full"
      style={{
        visibility:
          selectedDeck?.status === DeckStatus.Ready ? 'unset' : 'hidden'
      }}
    >
      <div
        role="tablist"
        className="tabs z-[1000] flex justify-center bg-neutral bg-opacity-80"
      >
        <a
          role="tab"
          onClick={() => {
            trackEvent(analyticsInstance, 'generate_form_click_file_tab', {
              deck_id: selectedDeck.id
            });
            setSelectedTab('file');
          }}
          className={`tab ${selectedTab === 'file' ? 'tab-active' : ''}`}
        >
          Uploaded File
        </a>
        {!props.hideCustomOptions && (
          <a
            role="tab"
            onClick={() => {
              trackEvent(
                analyticsInstance,
                'generate_form_click_objectives_tab',
                {
                  deck_id: selectedDeck.id
                }
              );

              setSelectedTab('objectives');
            }}
            className={`tab ${
              selectedTab === 'objectives' ? 'tab-active' : ''
            }`}
          >
            {selectedDeckLearningObjectives ? '✓' : '+'} Learning Objectives
          </a>
        )}
        {!props.hideCustomOptions && (
          <a
            role="tab"
            onClick={() => {
              trackEvent(
                analyticsInstance,
                'generate_form_click_instructions_tab',
                {
                  deck_id: selectedDeck.id
                }
              );
              setSelectedTab('instructions');
            }}
            className={`tab ${
              selectedTab === 'instructions' ? 'tab-active' : ''
            }`}
          >
            {' '}
            {selectedDeckCustomInstructions ? '✓' : '+'} Custom Instructions
          </a>
        )}
      </div>
    </div>
  );

  if (
    props.pages.some((url) =>
      url.includes('s3.us-east-1.amazonaws.com/upload')
    ) &&
    props.pages.every((page) => page in blobDictionary)
  ) {
    return (
      <div
        className={`relative hidden overflow-y-scroll bg-gray-500 ${props.visibleSize}:block`}
        style={{ width: props.width }}
      >
        <div className="flex flex-col gap-8 pb-8 pl-24 pr-24 pt-8">
          {props.pages.map((page) => (
            <img
              key={page}
              className={`relative border-gray-600`}
              src={URL.createObjectURL(blobDictionary[page])}
            />
          ))}
        </div>
      </div>
    );
  }

  // if (props.pages.length > 200) {
  //   return null;
  // }

  return (
    <div
      className={`relative hidden overflow-y-scroll bg-neutral ${props.visibleSize}:block`}
      style={{
        width: props.width
      }}
    >
      {options}
      {(!!props.loadingMessage ||
        props.pages.some((page) => !(page in blobDictionary))) && (
        <div className="absolute mt-16 flex w-full items-center justify-center">
          <Loader />
        </div>
      )}
      {!(
        props.loadingMessage ||
        props.pages.some((page) => !(page in blobDictionary))
      ) &&
        selectedTab === 'file' && (
          <div className="flex w-full flex-col gap-8 pb-8 pl-24 pr-24 pt-8">
            <div
              style={{
                visibility: 'hidden'
              }}
              className="file-container w-full"
            ></div>
            {width > 100 &&
              view.length > 3 &&
              pdfDocument &&
              props.pages.map((page, index) => {
                const pageNumber = index + 1;
                return (
                  <div
                    key={pageNumber}
                    style={{
                      scrollMargin: 10,
                      width: width,
                      height: width * (view[3] / view[2])
                    }}
                    id={'content_viewer_' + pageNumber}
                    className={`${
                      props.visiblePages &&
                      !props.visiblePages.includes(pageNumber)
                        ? 'opacity-20'
                        : ''
                    } relative border-gray-600`}
                  >
                    <div className="shadow-m bg-gray-60-0 absolute z-10 -ml-9 text-xs text-base-content">
                      {pageNumber}
                    </div>
                    <div className="relative z-0">
                      <div
                        style={{
                          maxWidth: '100%'
                        }}
                      >
                        <PageViewer
                          page={pageNumber}
                          pdfDocument={pdfDocument}
                          width={width}
                          height={width * (view[3] / view[2])}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      {selectedTab === 'objectives' && <LearningObjectives />}
      {selectedTab === 'instructions' && <CustomInstructions />}
    </div>
  );
}
